import BlueRibbonJackpotStatusComponent from "../../../components/blue_ribbon_jackpot_status_toaster/blue_ribbon_jackpot_status_toaster_component";
import BlueRibbonJackpotWinToasterComponent from "../../../components/blue_ribbon_jackpot_win_toaster/blue_ribbon_jp_win_toaster_component";
import {
    OptEvent,
    GameDetailsChangedEvent,
    GameStatusChangedEvent,
    PotStateChangedEvent,
    WinEvent,
    ComplexMatchDetails,
    GameObject
} from "../blue_ribbon_api_interfaces";
import { SYSTEM_EVENTS } from "../../../events/events";
import { POSITION } from "../../toaster/toaster_service_consts";
import BlueRibbonJackpotWelcomeComponent from "../../../components/blue_ribbon_jackpot_welcome/blue_ribbon_welcome_component";
import {
    IBlueRibbonEventsHandler
} from "./blue_ribbon_event_handler_interface";
import Event from '../../../utils/event';
import Device from '../../../utils/device/device';
import BaseObject from "../../../base/base_object";
import {
    BlueRibbonExecutionMonitor,
    clearBlueRibbonLocalStorageKey,
    convertToCents,
    getBlueRibbonLocalStorageKey,
    isValidJackpotValue,
    setBlueRibbonLocalStorageKey
} from '../blue_ribbon_helpers';
import ServiceFactory from '../../service_factory';
import {BlueRibbonPersonalJackpot} from '../jackpots/blue_ribbon_personal_jackpot';
import {BLUE_RIBBON_POT_STATE, IBlueRibbonPersonalJackpot} from '../../../../../common/interfaces/blue-ribbon-party-jackpot.interface';
import {IBlueRibbonConnection} from '../blue_ribbon_connection_interface';

const PersonalJackpotWelcomePopupShownStorageKey = 'BRPersonalJackpotWelcomePopupShown';

export default class BlueRibbonPersonalJackpotEventsHandler extends BaseObject implements IBlueRibbonEventsHandler {
    static CLASS = "BlueRibbonPersonalJackpotEventsHandler";

    private _welcomeToaster: BlueRibbonJackpotStatusComponent;
    private _winToaster: BlueRibbonJackpotWinToasterComponent;

    constructor(private _blueRibbonConnection: IBlueRibbonConnection,
                private _serviceFactory: ServiceFactory,
                private _getPersonalJackpotId: () => string) {
        super();
        this._executionMonitor = new BlueRibbonExecutionMonitor(this._serviceFactory);
    }

    private _executionMonitor: BlueRibbonExecutionMonitor;

    private _personalJackPot: BlueRibbonPersonalJackpot;
    get personalJackPot(): IBlueRibbonPersonalJackpot {
        return this._personalJackPot;
    }


    onConnected(matchedOperatorGames: ComplexMatchDetails[]) {
        this._personalJackPot = new BlueRibbonPersonalJackpot(this._getPersonalJackpotId(), this._serviceFactory);
        const personalJackpotExists = (0 <= matchedOperatorGames.findIndex(gameObj => this._isMyPersonalJackpot(gameObj.jackpotGameId)));
        if (!personalJackpotExists) {
            return;
        }

        this._fireStateChangedEvent();
    }

    private _onStartGamesFeedCount = 0;
    onGamesFeedStarted(data: GameObject[]): void {

        // we only show the toaster or the popup only if this is the first start games feed on this session
        this._onStartGamesFeedCount++;
        if(this._onStartGamesFeedCount > 1) {
            return;
        }

        this._blueRibbonConnection.isPlayerOptInToJackpotGame({
            gameId: this._personalJackPot.jackpotId
        }).then(data => {

            this._personalJackPot.setIsOptedIn(data.playerOptState.isOptIn);

            if(this._wasPersonalJackpotWelcomePopupShown()) {
                this._showWelcomeToaster();
            } else {
                // show opt in popup if player didn't contributed to this jackpot and he's eligible
                if (this._personalJackPot.isEligible && !this._personalJackPot.hasBetContributions) {
                    this._showOptInPopup();
                }
            }
        });

    }

    private _wasPersonalJackpotWelcomePopupShown(): boolean {
        return 'true' === getBlueRibbonLocalStorageKey(PersonalJackpotWelcomePopupShownStorageKey);
    }

    private _setPersonalJackpotWelcomePopUpShownInLocalStorage() {
        setBlueRibbonLocalStorageKey(PersonalJackpotWelcomePopupShownStorageKey, 'true');
    }

    private _clearPersonalJackpotWelcomePopUpShownFromLocalStorage() {
        clearBlueRibbonLocalStorageKey(PersonalJackpotWelcomePopupShownStorageKey);
    }

    dispose() {
        if (this._welcomeToaster) {
            this._welcomeToaster.closeToaster();
            this._welcomeToaster = null;
        }

        if (this._winToaster) {
            this._winToaster.closeToaster();
            this._winToaster = null;
        }
    }

    onOptIn(data: OptEvent) {
        if (!this._isMyPersonalJackpot(data.playerOptState.gameId)) {
            return;
        }

        this._personalJackPot.setIsOptedIn(true);
        this._fireStateChangedEvent();
        this._showOptInPopup();
    }

    private _isMyPersonalJackpot(jackpotId: string): boolean {
        return this._personalJackPot.jackpotId === jackpotId;
    }

    onOptOut(data: OptEvent) {
        if (!this._isMyPersonalJackpot(data.playerOptState.gameId)) {
            return;
        }

        this._personalJackPot.setIsOptedIn(false);
        this._fireStateChangedEvent();
    }

    onGameDetailsChanged(data: GameDetailsChangedEvent) {
        if (!this._isMyPersonalJackpot(data.gameDetails.gameId)) {
            return;
        }

        if (data.gameDetails.pots?.length >= 1) {
            this._personalJackPot.setInitialValue(convertToCents(data.gameDetails.pots[0].seedPotAmount));
        }
    }

    onGameStatusChanged(data: GameStatusChangedEvent) {
        if (!this._isMyPersonalJackpot( data.gameStatus.gameId)) {
            return;
        }

        this._personalJackPot.setIsActive(data.gameStatus.status === 'ACTIVE', data.gameStatus.inactiveReason);
        this._fireStateChangedEvent();
    }

    onPotStateChanged(data: PotStateChangedEvent) {
        if (!this._isMyPersonalJackpot(data.jackpotPotState.gameId)) {
            return;
        }

        this._personalJackPot.setPotState(data.jackpotPotState.potStatus as BLUE_RIBBON_POT_STATE);
        this._personalJackPot.setCurrentValue(convertToCents(data.jackpotPotState.progressive));
        this._fireStateChangedEvent();
    }

    onWin(data: WinEvent) {
        if (!this._isMyPersonalJackpot(data.winningDetails.gameId)) {
            return;
        }

        this._personalJackPot.setWinAmount(data.winningDetails.currency, convertToCents(data.winningDetails.amount), data.winningDetails.playerId);
        this._fireStateChangedEvent();
        this._showWinToaster();
    }

    private _fireStateChangedEvent() {
        Event.fire(SYSTEM_EVENTS.BLUE_RIBBON_PERSONAL_JP_STATE_CHANGED);
    }

    private _showWelcomeToaster() {
        if (!this._personalJackPot.isEligible) {
            return;
        }


        if (!isValidJackpotValue(this._personalJackPot.currentValue)) {
            this._executionMonitor.error(`_showWelcomeToaster: Current Jackpot value ${this._personalJackPot.currentValue} is invalid`);
            //this.operations.sendElasticError("showWelcomeToaster", "invalidJPValue", this._personalJackPotInfo.currentValue);
            return;
        }

        this._welcomeToaster = this._serviceFactory.componentFactoryService().createComponent(BlueRibbonJackpotStatusComponent, {
            jackPotValue: this._personalJackPot.currentValue
        });

        const position: keyof typeof POSITION = !Device.os.isMobileOS? "BOTTOM_RIGHT" : "TOP_CENTER";
        this._welcomeToaster.showAsToaster({
            timeoutInSecs: 7,
            position: position,
            additionalClass: 'cgp-toaster-component-no-padding cgp-toaster-component-primary-border'
        });
    }

    private _showWinToaster() {
        this._winToaster = this._serviceFactory.componentFactoryService().createComponent(BlueRibbonJackpotWinToasterComponent, {
            wonAmount: this._personalJackPot.winAmount
        });

        const position: keyof typeof POSITION = !Device.os.isMobileOS? "BOTTOM_RIGHT" : "TOP_CENTER";
        this._winToaster.showAsToaster({
            timeoutInSecs: 15,
            position: position,
            close: false,
            additionalClass: 'cgp-toaster-component-no-padding cgp-toaster-component-secondary-border'
        });
    }

    private _showOptInPopup() {
        if (this._personalJackPot.isEligible) {
            // popup auto-hides by clicking the buttons or react itself to OVERLAY messages
            const welcomePopup = this._serviceFactory.componentFactoryService().createComponent(BlueRibbonJackpotWelcomeComponent);
            welcomePopup.showPopup();
            this._setPersonalJackpotWelcomePopUpShownInLocalStorage();
        }
    }

}
